import i18n from "@core/i18n";

import type { DraftSummary, SettingDescriptor } from "./types";

/**
 * Source des labels humains pour les enums `audienceMode` / `resultsVisibility`.
 * Garde le même mapping que `PollSettingsForm` pour cohérence.
 */
const AUDIENCE_MODE_LABEL: Record<string, string> = {
  link: i18n.t("draft_summary.audience.link.label"),
  group: i18n.t("draft_summary.audience.group.label"),
  emails: i18n.t("draft_summary.audience.emails.label"),
  email: i18n.t("draft_summary.audience.emails.label"), // alias historique
};

const RESULTS_VISIBILITY_LABEL: Record<string, string> = {
  public: i18n.t("draft_summary.results_visibility.public.label"),
  voters: i18n.t("draft_summary.results_visibility.voters.label"),
  "creator-only": i18n.t("draft_summary.results_visibility.creator_only.label"),
  restricted: i18n.t("draft_summary.results_visibility.restricted.label"),
};

const AUDIENCE_MODE_SUB: Record<string, string> = {
  link: i18n.t("draft_summary.audience.link.sub"),
  group: i18n.t("draft_summary.audience.group.sub"),
  emails: i18n.t("draft_summary.audience.emails.sub"),
  email: i18n.t("draft_summary.audience.emails.sub"),
};

const RESULTS_VISIBILITY_SUB: Record<string, string> = {
  public: i18n.t("draft_summary.results_visibility.public.sub"),
  voters: i18n.t("draft_summary.results_visibility.voters.sub"),
  "creator-only": i18n.t("draft_summary.results_visibility.creator_only.sub"),
  restricted: i18n.t("draft_summary.results_visibility.restricted.sub"),
};

const DEFAULT_AUDIENCE_MODE = "link";
const DEFAULT_RESULTS_VISIBILITY = "public";

export interface BuildDraftInput {
  title: string;
  selectedDatesCount: number;
  timeSlotsCount: number;
  participantEmailsCount: number;
  advancedSettings: Record<string, any>;
}

/**
 * Construit un `DraftSummary` immutable à partir du state du `PollCreator`.
 * Détermine `isEmpty` et `isDefault` selon les valeurs. Ne calcule PAS les
 * hints — ça reste `useSettingsHints` (PR 2).
 */
export function buildDraftSummary(input: BuildDraftInput): DraftSummary {
  const a = input.advancedSettings ?? {};
  const audienceMode = String(a.audienceMode ?? DEFAULT_AUDIENCE_MODE);
  const resultsVisibility = String(
    a.resultsVisibility ?? DEFAULT_RESULTS_VISIBILITY,
  );

  const settings: SettingDescriptor[] = [];

  // Groupe IMPORTANT — toujours visibles
  settings.push({
    key: "audienceMode",
    group: "important",
    icon: "🔗",
    label: i18n.t("draft_summary.audience.title"),
    value: audienceMode,
    displayValue: AUDIENCE_MODE_LABEL[audienceMode] ?? audienceMode,
    subDescription: AUDIENCE_MODE_SUB[audienceMode],
    isDefault: audienceMode === DEFAULT_AUDIENCE_MODE,
    isEmpty: false,
    type: "enum",
    options: [
      {
        id: "group",
        icon: "👥",
        label: i18n.t("draft_summary.audience.group.label"),
        description: i18n.t("draft_summary.audience.group.sub"),
      },
      {
        id: "emails",
        icon: "✉️",
        label: i18n.t("draft_summary.audience.emails.label"),
        description: i18n.t("draft_summary.audience.emails.option_description"),
      },
      {
        id: "link",
        icon: "🔗",
        label: i18n.t("draft_summary.audience.link.label"),
        description: i18n.t("draft_summary.audience.link.option_description"),
      },
    ],
  });

  settings.push({
    key: "resultsVisibility",
    group: "important",
    icon: "👁",
    label: i18n.t("draft_summary.results_visibility.title"),
    value: resultsVisibility,
    displayValue:
      RESULTS_VISIBILITY_LABEL[resultsVisibility] ?? resultsVisibility,
    subDescription: RESULTS_VISIBILITY_SUB[resultsVisibility],
    isDefault: resultsVisibility === DEFAULT_RESULTS_VISIBILITY,
    isEmpty: false,
    type: "enum",
    options: [
      {
        id: "public",
        icon: "🌐",
        label: i18n.t("draft_summary.results_visibility.public.label"),
        description: i18n.t("draft_summary.results_visibility.public.sub"),
      },
      {
        id: "voters",
        icon: "👥",
        label: i18n.t("draft_summary.results_visibility.voters.label"),
        description: i18n.t("draft_summary.results_visibility.voters.sub"),
      },
      {
        id: "creator-only",
        icon: "🔒",
        label: i18n.t("draft_summary.results_visibility.creator_only.label"),
        description: i18n.t(
          "draft_summary.results_visibility.creator_only.sub",
        ),
      },
    ],
  });

  // Groupe HINT — toujours rendus en read-only PR 1, les hints calculés
  // viendront en PR 2 via `useSettingsHints`.
  const location = (a.location ?? "").trim?.() || "";
  settings.push({
    key: "location",
    group: "hint",
    icon: "📍",
    label: i18n.t("draft_summary.location.label"),
    value: location || null,
    displayValue: location || i18n.t("draft_summary.location.unspecified"),
    isEmpty: location.length === 0,
    type: "string",
  });

  const expiresAt = a.expiresAt as string | undefined | null;
  settings.push({
    key: "expiresAt",
    group: "hint",
    icon: "⏰",
    label: i18n.t("draft_summary.expires_at.label"),
    value: expiresAt ?? null,
    displayValue: expiresAt
      ? formatDateHuman(expiresAt)
      : i18n.t("draft_summary.expires_at.no_limit"),
    isEmpty: !expiresAt,
    type: "date",
  });

  // Groupe AVANCÉ
  settings.push({
    key: "sendNotifications",
    group: "advanced",
    icon: "📧",
    label: i18n.t("draft_summary.send_notifications.label"),
    value: String(!!a.sendNotifications),
    displayValue: a.sendNotifications
      ? i18n.t("draft_summary.enabled")
      : i18n.t("draft_summary.disabled"),
    isDefault: !a.sendNotifications,
    isEmpty: false,
    type: "boolean",
  });

  settings.push({
    key: "requireAuth",
    group: "advanced",
    icon: "🔒",
    label: i18n.t("draft_summary.require_auth.label"),
    value: String(!!a.requireAuth),
    displayValue: a.requireAuth
      ? i18n.t("draft_summary.yes")
      : i18n.t("draft_summary.no"),
    isDefault: !a.requireAuth,
    isEmpty: false,
    type: "boolean",
  });

  settings.push({
    key: "oneResponsePerPerson",
    group: "advanced",
    icon: "🧍",
    label: i18n.t("draft_summary.one_response_per_person.label"),
    value: String(!!a.oneResponsePerPerson),
    displayValue: a.oneResponsePerPerson
      ? i18n.t("draft_summary.yes")
      : i18n.t("draft_summary.no"),
    isDefault: !a.oneResponsePerPerson,
    isEmpty: false,
    type: "boolean",
  });

  settings.push({
    key: "allowEditAfterSubmit",
    group: "advanced",
    icon: "✏️",
    label: i18n.t("draft_summary.allow_edit_after_submit.label"),
    value: String(!!a.allowEditAfterSubmit),
    displayValue: a.allowEditAfterSubmit
      ? i18n.t("draft_summary.allowed")
      : i18n.t("draft_summary.no"),
    isDefault: !!a.allowEditAfterSubmit, // default true → c'est le défaut
    isEmpty: false,
    type: "boolean",
  });

  const maxResponses = a.maxResponses;
  settings.push({
    key: "maxResponses",
    group: "advanced",
    icon: "🔢",
    label: i18n.t("draft_summary.max_responses.label"),
    value: maxResponses != null ? String(maxResponses) : null,
    displayValue:
      maxResponses != null
        ? String(maxResponses)
        : i18n.t("draft_summary.max_responses.unlimited"),
    isEmpty: maxResponses == null,
    type: "string",
  });

  return {
    title: input.title,
    selectedDatesCount: input.selectedDatesCount,
    timeSlotsCount: input.timeSlotsCount,
    participantEmailsCount: input.participantEmailsCount,
    settings,
  };
}

function formatDateHuman(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(i18n.language || "fr", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
}
